/**
 * Field-side listener for system/* envelopes the operator broadcasts.
 *
 *   - `system/recording.started` → flip local consent.recording to
 *     "granted" so ConsentHud's REC pill lights up on the field's HUD.
 *   - `system/recording.stopped` → flip back to "revoked".
 *   - `system/session.ended`     → disconnect from LiveKit + flag the
 *     UI to render the "session ended" screen.
 *
 * Also listens for the LiveKit `Disconnected` event (fires when the
 * backend deletes the room via the LiveKit Server API on /sessions/
 * {id}/end) and flags `useSessionEndedStore` accordingly. Either path
 * lands on the same end-screen.
 */
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import { useEffect } from "react";
import { create } from "zustand";

import { useConsentStore } from "@/lib/consent";
import { useRecordingStore } from "@/lib/recording";
import { useSessionBus } from "@/lib/sessionBus";
import { playSound } from "@/lib/soundEffects";

type EndedStore = {
  ended: boolean;
  reason: string | null;
  set: (v: boolean, reason?: string) => void;
  reset: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components -- store co-located with handler; HMR limitation is acceptable here.
export const useSessionEndedStore = create<EndedStore>((set) => ({
  ended: false,
  reason: null,
  set: (v, reason) => set({ ended: v, reason: reason ?? null }),
  reset: () => set({ ended: false, reason: null }),
}));


type Props = { sessionId: string };

export function FieldSystemHandler({ sessionId: _sessionId }: Props) {
  const room = useRoomContext();
  const bus = useSessionBus((s) => s.bus);
  const setConsent = useConsentStore((s) => s.set);
  const flagEnded = useSessionEndedStore((s) => s.set);
  const setRecording = useRecordingStore((s) => s.set);

  // System envelopes from the operator.
  useEffect(() => {
    if (!bus) return;
    const offRecStart = bus.on<{ at?: number }, void>(
      "system/recording.started",
      ({ payload }) => {
        setConsent("recording", "granted");
        // `at` is the operator's wall clock at start (epoch ms),
        // populated in RecordingControl.start.onSuccess. Falls back
        // to now() if missing (shouldn't happen but defensive).
        setRecording(true, payload?.at ?? Date.now());
        void playSound("rec-start");
      },
    );
    const offRecPause = bus.on<{ at?: number }, void>(
      "system/recording.paused",
      ({ payload }) => {
        useRecordingStore.getState().pause(payload?.at ?? Date.now());
        void playSound("rec-pause");
      },
    );
    const offRecResume = bus.on<{ at?: number }, void>(
      "system/recording.resumed",
      ({ payload }) => {
        useRecordingStore.getState().resume(payload?.at ?? Date.now());
        void playSound("rec-resume");
      },
    );
    const offRecStop = bus.on("system/recording.stopped", () => {
      setConsent("recording", "revoked");
      useRecordingStore.getState().reset();
      void playSound("rec-stop");
    });
    const offEnded = bus.on("system/session.ended", () => {
      flagEnded(true, "operator ended the session");
      void room?.disconnect();
    });
    return () => {
      offRecStart();
      offRecPause();
      offRecResume();
      offRecStop();
      offEnded();
    };
  }, [bus, setConsent, flagEnded, setRecording, room]);

  // Backend can also force the disconnect via LiveKit's delete_room API
  // when /sessions/{id}/end fires — listen for the LiveKit-side event so
  // the field still lands on the end-screen even if the system envelope
  // got dropped.
  useEffect(() => {
    if (!room) return;
    const onDisconnect = () => flagEnded(true, "disconnected");
    room.on(RoomEvent.Disconnected, onDisconnect);
    return () => {
      room.off(RoomEvent.Disconnected, onDisconnect);
    };
  }, [room, flagEnded]);

  return null;
}